/**
 * Dado hierárquico — categoria com subcategoria, estrutura de produto, menu.
 *
 * É uma das duas classes de tela que hoje **só** existem como componente escrito
 * à mão: sem este tipo, todo JSON aninhado que não é tabular sai do gerador sem
 * ter para onde ir.
 *
 * **O que é declarado, e não codificado:** onde estão os filhos, o rótulo e o
 * identificador dentro de cada item. É isso que permite a mesma árvore desenhar
 * um JSON com `children` e outro com `subcategorias` sem uma linha nova.
 *
 * A conversão para o formato do `nz-tree` é pura e mora em `tree-nodes.ts`, com
 * teste próprio: é onde um `childrenPath` errado deixa a árvore rasa em vez de
 * dar erro, e isso é o tipo de defeito que ninguém associa à configuração.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTreeModule, NzTreeNodeOptions } from 'ng-zorro-antd/tree';
import { NzEmptyModule } from 'ng-zorro-antd/empty';

import { ScreenNode } from '../../model/node-props';
import { resolvePath } from '../../runtime/data-binding';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { ScreenContext } from '../screen-context';
import { TranslateTAPipe } from '../translate-ta.pipe';
import { toTreeNodes } from './tree-nodes';

@Component({
  selector: 'tem-treeview-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzEmptyModule,
    NzInputModule,
    NzTreeModule,
    TranslateTAPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tem-tree">

      @if (isSearchable()) {
        <input
          nz-input
          nzSize="small"
          type="search"
          [ngModel]="search()"
          (ngModelChange)="search.set($event)"
          [placeholder]="'builderScreen.treeview.search' | translateTA" />
      }

      @if (nodes().length > 0) {
        <nz-tree
          [nzData]="nodes()"
          [nzCheckable]="isSelectable()"
          [nzSearchValue]="search()"
          [nzDraggable]="isDraggable()"
          [nzCheckStrictly]="!cascades()"
          (nzCheckBoxChange)="onCheck()"></nz-tree>
      } @else {
        <nz-empty
          [nzNotFoundContent]="'builderScreen.treeview.empty' | translateTA" />
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .tem-tree {
      display: flex;
      flex-direction: column;
      gap: var(--tem-space-2);
    }
  `]
})
export class TreeViewFieldRenderer implements NodeRenderer<'treeview'> {

  readonly node = input.required<ScreenNode<'treeview'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly context = inject(ScreenContext, { optional: true });

  readonly search = signal('');

  readonly isSelectable = computed<boolean>(() => this.node().props.selectable === true);
  readonly isDraggable = computed<boolean>(() => this.node().props.draggable === true);
  readonly isSearchable = computed<boolean>(() => this.node().props.searchable === true);

  /**
   * Marcar o pai marca os filhos?
   *
   * `nzCheckStrictly` é o INVERSO: ligado, pai e filho são independentes. O
   * nome invertido é do NG-Zorro, e converter aqui evita que quem lê o
   * template precise fazer a inversão de cabeça.
   */
  readonly cascades = computed<boolean>(
    () => this.node().props.cascadeSelection === true
  );

  readonly nodes = computed<NzTreeNodeOptions[]>(() => {
    const props = this.node().props;
    const caminho = this.node().binding?.dataPath;

    if (caminho === undefined || this.context === null) {
      return [];
    }

    const dados = resolvePath(this.context.data(), caminho);

    return toTreeNodes(dados, {
      childrenPath: props.childrenPath,
      labelPath: props.labelPath,
      idPath: props.idPath,
      expandToLevel: props.expandToLevel ?? 0
    });
  });

  /**
   * A seleção ainda não volta para o formulário.
   *
   * Está anotado como limitação em vez de meio implementado: gravar a seleção
   * exige decidir o formato (lista de ids? subárvore?), e essa decisão pertence
   * ao caso de uso que ainda não existe. Marcar sem gravar é honesto; gravar num
   * formato inventado seria dívida com aparência de funcionalidade.
   */
  onCheck(): void {
    return;
  }
}
